import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ThumbsUp, MessageSquare, CheckCircle2, ChevronDown, ChevronUp, Gamepad2, Search } from 'lucide-react';
import TipTapEditor from '@/components/TipTapEditor';
import { useAuth } from '@/context/AuthContext';
import { showError, showSuccess } from '@/utils/toast';
import {
  getDefaultRequestDescription,
  GAME_REQUEST_IMAGE_MAX_SIZE,
  GAME_REQUEST_IMAGE_TYPES,
  DuplicateCheckResult,
  GameRequestSummary,
  STEAM_APP_URL_PATTERN,
  SteamLookupResult,
  extractErrorMessage,
  formatGameRequestDate,
  getCsrfToken,
} from '@/utils/gameRequests';
import GameRequestAccessGate from '@/components/GameRequestAccessGate';
import GameRequestPlatformPicker from '@/components/GameRequestPlatformPicker';
import { GAME_REQUEST_VERSION_MAX_LENGTH } from '@/utils/gameRequests';

const GameRequests: React.FC = () => {
  const navigate = useNavigate();
  const { isLoggedIn, isAdmin, isMuted, currentUser, loading: authLoading } = useAuth();
  const canAccess = isLoggedIn && !!currentUser?.name;

  const [requests, setRequests] = useState<GameRequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [gameName, setGameName] = useState('');
  const [steamLink, setSteamLink] = useState('');
  const [otherLink, setOtherLink] = useState('');
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [usedPlatforms, setUsedPlatforms] = useState<string[]>([]);
  const [version, setVersion] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [description, setDescription] = useState('');

  const requesterName = currentUser?.translaterName || currentUser?.alias || currentUser?.name || '';

  useEffect(() => {
    setDescription(getDefaultRequestDescription(requesterName));
  }, [requesterName]);

  const [steamLookup, setSteamLookup] = useState<SteamLookupResult | null>(null);
  const [steamLookupLoading, setSteamLookupLoading] = useState(false);
  const [steamLookupError, setSteamLookupError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateCheckResult | null>(null);

  const fetchRequests = useCallback(async (query: string, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    const response = await fetch(`/api/game_requests?${params.toString()}`, {
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok) throw new Error('load failed');
    setRequests(await response.json());
  }, []);

  const loadUsedPlatforms = useCallback(async () => {
    try {
      const response = await fetch('/api/game_requests/platforms', { headers: { Accept: 'application/json' } });
      if (response.ok) setUsedPlatforms(await response.json());
    } catch {
      // A korábbi platformok csak javaslatok, hiányuk nem akadályozza a kérést.
    }
  }, []);

  const refreshRequests = useCallback(async () => {
    try {
      await fetchRequests(searchQuery);
    } catch {
      showError('Nem sikerült betölteni a kéréseket.');
    }
  }, [fetchRequests, searchQuery]);

  useEffect(() => {
    if (canAccess) loadUsedPlatforms();
  }, [canAccess, loadUsedPlatforms]);

  useEffect(() => {
    if (!canAccess) return;
    const timer = window.setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [canAccess, searchInput]);

  useEffect(() => {
    if (!canAccess) return;
    const controller = new AbortController();
    fetchRequests(searchQuery, controller.signal)
      .catch(err => {
        if ((err as Error).name !== 'AbortError') showError('Nem sikerült betölteni a kéréseket.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [canAccess, searchQuery, fetchRequests]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  useEffect(() => {
    const link = steamLink.trim();
    if (!STEAM_APP_URL_PATTERN.test(link)) {
      setSteamLookup(null);
      setSteamLookupError(null);
      setSteamLookupLoading(false);
      return;
    }

    const controller = new AbortController();
    setSteamLookupLoading(true);
    setSteamLookupError(null);

    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/game_requests/steam_lookup?steam_link=${encodeURIComponent(link)}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) {
          setSteamLookup(null);
          setSteamLookupError(await extractErrorMessage(response, 'Nem sikerült lekérni a Steam adatokat.'));
          return;
        }
        const data: SteamLookupResult = await response.json();
        setSteamLookup(data);
        setGameName(data.name);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setSteamLookup(null);
        setSteamLookupError('Hálózati hiba a Steam adatok lekérésekor.');
      } finally {
        if (!controller.signal.aborted) setSteamLookupLoading(false);
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [steamLink]);

  useEffect(() => {
    const link = steamLink.trim();
    if (!STEAM_APP_URL_PATTERN.test(link)) {
      setDuplicates(null);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/game_requests/duplicates?steam_link=${encodeURIComponent(link)}`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        setDuplicates(response.ok ? await response.json() : null);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setDuplicates(null);
      }
    }, 500);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [steamLink]);

  const isDuplicateBlocked = !!duplicates?.game || !!duplicates?.request;

  const resetForm = () => {
    setGameName('');
    setSteamLink('');
    setOtherLink('');
    setPlatforms([]);
    setVersion('');
    setImageFile(null);
    setDescription(getDefaultRequestDescription(requesterName));
    setSteamLookup(null);
    setSteamLookupError(null);
    setDuplicates(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && file.size > GAME_REQUEST_IMAGE_MAX_SIZE) {
      showError('A kép legfeljebb 5 MB lehet.');
      e.target.value = '';
      setImageFile(null);
      return;
    }
    setImageFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      showError('A kéréshez be kell jelentkezned.');
      return;
    }
    const hasSteamLink = steamLink.trim() !== '';
    if (isDuplicateBlocked) {
      showError('Erre a játékra már van fordítás vagy nyitott kérés.');
      return;
    }
    if (!gameName.trim()) {
      showError('A játék neve kötelező.');
      return;
    }
    if (platforms.length === 0) {
      showError('Legalább egy platformot ki kell választani.');
      return;
    }
    if (!version.trim()) {
      showError('A verzió megadása kötelező.');
      return;
    }
    if (!hasSteamLink && !otherLink.trim()) {
      showError('Steam link hiányában egyéb linket kell megadni.');
      return;
    }
    if (!hasSteamLink && !imageFile) {
      showError('Steam link hiányában képet kell feltölteni.');
      return;
    }

    const formData = new FormData();
    formData.append('game_request[game_name]', gameName.trim());
    formData.append('game_request[steam_link]', steamLink.trim());
    platforms.forEach(platform => formData.append('game_request[platforms][]', platform));
    formData.append('game_request[version]', version.trim());
    formData.append('game_request[description]', description);
    if (!hasSteamLink) {
      formData.append('game_request[other_link]', otherLink.trim());
      if (imageFile) formData.append('game_request[image]', imageFile);
    }

    setSubmitting(true);
    try {
      const response = await fetch('/api/game_requests', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          Accept: 'application/json',
        },
        body: formData,
      });

      if (!response.ok) {
        showError(await extractErrorMessage(response, 'Hiba történt a kérés mentésekor.'));
        return;
      }

      showSuccess('A kérésed elmentve.');
      resetForm();
      setIsPanelOpen(false);
      await Promise.all([refreshRequests(), loadUsedPlatforms()]);
    } catch {
      showError('Hálózati hiba történt.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      showError('A szavazáshoz be kell jelentkezned.');
      return;
    }
    try {
      const response = await fetch(`/api/game_requests/${id}/vote`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken(), Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('vote failed');
      await refreshRequests();
    } catch {
      showError('Nem sikerült a szavazás.');
    }
  };

  const handleComplete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      const response = await fetch(`/api/game_requests/${id}/complete`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken(), Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('complete failed');
      await refreshRequests();
    } catch {
      showError('Nem sikerült módosítani az állapotot.');
    }
  };

  const canRequest = canAccess && !isMuted;

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Betöltés...</div>;
  }

  if (!canAccess) {
    return <GameRequestAccessGate needsLogin={!isLoggedIn} />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="sticky top-0 z-50 w-full">
        <Header />
      </div>
      <main className="flex-grow container mx-auto p-4 py-8 main-content-bg mt-[12px] max-w-4xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Gamepad2 className="h-8 w-8" /> Kérések
          </h1>
          <Button
            onClick={() => setIsPanelOpen(open => !open)}
            disabled={!canRequest && !isPanelOpen}
            title={isMuted ? 'Némítva vagy' : undefined}
          >
            Új játék kérés
            {isPanelOpen ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
          </Button>
        </div>

        {isPanelOpen && (
          <Card className="mb-8 bg-card border-border shadow-md">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="game-request-steam-link" className="mb-2 block">
                    Steam link <span className="text-muted-foreground font-normal">(ha van)</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="game-request-steam-link"
                      type="url"
                      placeholder="https://store.steampowered.com/app/..."
                      value={steamLink}
                      onChange={e => setSteamLink(e.target.value)}
                    />
                    {steamLink && (
                      <Button type="button" variant="ghost" onClick={() => setSteamLink('')}>
                        Törlés
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Ha van, add meg a játék Steam linkjét, és a Steam adatait automatikusan betöltjük. Ha nincs, ugord át.
                  </p>
                  {steamLookupLoading && (
                    <p className="text-sm text-muted-foreground mt-2">Steam adatok lekérése...</p>
                  )}
                  {steamLookupError && (
                    <p className="text-sm text-red-500 mt-2">{steamLookupError}</p>
                  )}
                  {duplicates?.game && (
                    <div className="mt-3 rounded border border-amber-500 bg-amber-500/10 p-3 text-sm">
                      A játék már megvan nálunk: <Link to={duplicates.game.path} className="font-semibold text-primary hover:underline">{duplicates.game.name}</Link>, ehhez már van fordítás.
                    </div>
                  )}
                  {duplicates?.request && (
                    <div className="mt-3 rounded border border-red-500 bg-red-500/10 p-3 text-sm">
                      Erre a játékra már van nyitott kérés: <Link to={duplicates.request.path} className="font-semibold text-primary hover:underline">{duplicates.request.game_name}</Link>. Új kérés nem küldhető, a meglévőhöz szólj hozzá vagy szavazz rá.
                    </div>
                  )}
                  {steamLookup && (
                    <div className="mt-3 flex items-center gap-3 rounded border border-border p-2">
                      {steamLookup.image_url ? (
                        <img src={steamLookup.image_url} alt={steamLookup.name} className="w-40 h-20 shrink-0 object-cover rounded" />
                      ) : (
                        <div className="w-40 h-20 shrink-0 rounded bg-secondary flex items-center justify-center">
                          <Gamepad2 className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <span className="font-medium">{steamLookup.name}</span>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="game-request-name" className="mb-2 block">
                    Játék neve <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="game-request-name"
                    value={gameName}
                    maxLength={255}
                    required
                    onChange={e => setGameName(e.target.value)}
                    placeholder="Pl. Hades II"
                  />
                </div>

                <div>
                  <Label className="mb-2 block">
                    Platform <span className="text-red-500">*</span>
                  </Label>
                  <GameRequestPlatformPicker
                    selected={platforms}
                    onChange={setPlatforms}
                    usedPlatforms={usedPlatforms}
                  />
                </div>

                <div>
                  <Label htmlFor="game-request-version" className="mb-2 block">
                    Verzió <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="game-request-version"
                    value={version}
                    maxLength={GAME_REQUEST_VERSION_MAX_LENGTH}
                    required
                    onChange={e => setVersion(e.target.value)}
                    placeholder="Pl. 1.0.2, v2, Steam build 1234"
                  />
                </div>

                {!steamLink.trim() && (
                  <>
                    <div>
                      <Label htmlFor="game-request-other-link" className="mb-2 block">
                        Egyéb link <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="game-request-other-link"
                        type="url"
                        placeholder="https://..."
                        value={otherLink}
                        required
                        onChange={e => setOtherLink(e.target.value)}
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Steam link nélkül kötelező: pl. a játék weboldala vagy boltja.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="game-request-image" className="mb-2 block">
                        Kép <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="game-request-image"
                        type="file"
                        accept={GAME_REQUEST_IMAGE_TYPES.join(',')}
                        required={!imageFile}
                        onChange={handleImageChange}
                      />
                      <p className="text-sm text-muted-foreground mt-1">JPG, PNG, WEBP vagy GIF, legfeljebb 5 MB.</p>
                      {imagePreviewUrl && (
                        <img src={imagePreviewUrl} alt="Előnézet" className="mt-3 w-40 h-20 object-cover rounded border border-border" />
                      )}
                    </div>
                  </>
                )}

                <div>
                  <Label className="mb-2 block">Leírás</Label>
                  <TipTapEditor
                    value={description}
                    onChange={setDescription}
                    placeholder="Írd le, mit szeretnél (pl. magyar felirat, frissítés részletei)..."
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      resetForm();
                      setIsPanelOpen(false);
                    }}
                  >
                    Mégse
                  </Button>
                  <Button type="submit" disabled={submitting || isDuplicateBlocked}>
                    {submitting ? 'Küldés...' : 'Kérés küldése'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Keresés a kérések között..."
            aria-label="Keresés a kérések között"
            className="pl-9"
          />
        </div>

        {loading ? (
          <p className="text-center text-muted-foreground py-10">Betöltés...</p>
        ) : requests.length === 0 ? (
          <p className="text-center text-muted-foreground py-10">
            {searchQuery ? 'Nincs találat a keresésre.' : 'Még nincs kérés.'}
          </p>
        ) : (
          <ul className="space-y-3">
            {requests.map(item => (
              <li key={item.id}>
                <Card
                  onClick={() => navigate(`/keres/${item.id}`)}
                  className={`cursor-pointer hover:border-primary transition-colors bg-card border-border ${item.completed ? 'opacity-60' : ''}`}
                >
                  <CardContent className="p-4 flex gap-4 items-center">
                    <div className="w-32 h-16 shrink-0 rounded overflow-hidden bg-secondary flex items-center justify-center">
                      {item.image_url ? (
                        <img src={item.image_url} alt={item.game_name} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <Gamepad2 className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-semibold text-lg truncate">{item.game_name}</h2>
                        <Badge variant="secondary">{item.request_type === 'update_request' ? 'Frissítés' : 'Játék kérés'}</Badge>
                        {item.completed && (
                          <Badge className="bg-green-600 text-white">Elkészült</Badge>
                        )}
                        {item.game && (
                          <Link
                            to={item.game.path}
                            onClick={e => e.stopPropagation()}
                            className="text-sm font-semibold text-primary hover:underline"
                          >
                            Játék oldala →
                          </Link>
                        )}
                      </div>
                      {(item.platforms.length > 0 || item.version) && (
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                          {item.platforms.map(platform => (
                            <Badge key={platform} variant="outline" className="text-xs">{platform}</Badge>
                          ))}
                          {item.version && (
                            <span className="text-xs text-muted-foreground ml-1">Verzió: {item.version}</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={item.user?.avatar_url ?? undefined} alt={item.user?.username} />
                          <AvatarFallback className="text-xs">{item.user?.username?.charAt(0).toUpperCase() ?? '?'}</AvatarFallback>
                        </Avatar>
                        <span>{item.user?.username ?? 'Ismeretlen'}</span>
                        <span>•</span>
                        <span>{formatGameRequestDate(item.created_at)}</span>
                      </div>
                    </div>

                    <div className="flex flex-col items-center gap-2 shrink-0">
                      <Button
                        variant={item.voted ? 'default' : 'outline'}
                        size="sm"
                        onClick={e => handleVote(e, item.id)}
                        aria-label="Szavazás"
                        className="min-w-16"
                      >
                        <ThumbsUp className="h-4 w-4 mr-1" /> {item.votes_count}
                      </Button>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MessageSquare className="h-3 w-3" /> {item.comments_count}
                      </span>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={e => handleComplete(e, item.id)}
                          aria-label={item.completed ? 'Visszanyitás' : 'Elkészültnek jelölés'}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          {item.completed ? 'Visszanyit' : 'Kész'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default GameRequests;
